import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";

const cache = new Map();
const WINDOW = 8; // seconds visible in the detail strip
const b64 = (s) => { if (!s) return new Uint8Array(0); const bin = atob(s); const a = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) a[i] = bin.charCodeAt(i); return a; };

const useWave = (id) => {
  const [wave, setWave] = useState(null);
  useEffect(() => {
    if (!id) return undefined;
    if (cache.has(id)) { setWave(cache.get(id)); return undefined; }
    let alive = true;
    setWave(null);
    api.get(`/mixes/${id}/peaks`).then((r) => {
      const d = r.data || {};
      const w = { peaks: d.peaks || [], bass: d.bass || [], rate: d.rate || 20, hi: b64(d.hi), hiBass: b64(d.hi_bass) };
      cache.set(id, w);
      if (alive) setWave(w);
    }).catch(() => {});
    return () => { alive = false; };
  }, [id]);
  return wave;
};

// Find where the first beat lands by matching the BPM grid to bass hits in the first two minutes.
const beatOffset = (wave, beat) => {
  if (!wave?.hiBass.length) return 0;
  const { hiBass: b, rate } = wave;
  const lim = Math.min(b.length, rate * 120);
  let best = 0; let bestScore = -1;
  for (let o = 0; o < beat * rate; o += 1) {
    let s = 0;
    for (let x = o; x < lim; x += beat * rate) s += b[Math.round(x)] || 0;
    if (s > bestScore) { bestScore = s; best = o / rate; }
  }
  return best;
};

const useSize = (ref) => {
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
};

const prep = (c, w) => {
  const dpr = window.devicePixelRatio || 1;
  const h = c.clientHeight;
  if (c.width !== Math.round(w * dpr)) { c.width = Math.round(w * dpr); c.height = Math.round(h * dpr); }
  const g = c.getContext("2d");
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  g.clearRect(0, 0, w, h);
  return [g, h];
};

const DetailStrip = ({ wave, audioRef, bpm, onSeek }) => {
  const ref = useRef(null);
  const w = useSize(ref);
  const beat = 60 / bpm;
  const offset = useMemo(() => beatOffset(wave, beat), [wave, beat]);

  useEffect(() => {
    const c = ref.current;
    if (!c || !w) return undefined;
    let id;
    const draw = () => {
      const [g, h] = prep(c, w);
      const t = audioRef.current?.currentTime || 0;
      const start = t - WINDOW / 2;
      const px = w / WINDOW;
      const mid = h / 2;
      // beat grid
      const firstBeat = Math.ceil((start - offset) / beat);
      for (let n = firstBeat; offset + n * beat < start + WINDOW; n++) {
        if (n < 0) continue;
        const x = (offset + n * beat - start) * px;
        const bar = n % 4 === 0;
        g.fillStyle = bar ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.12)";
        g.fillRect(Math.round(x), 0, bar ? 1.5 : 1, h);
        if (bar && n % 16 === 0) {
          g.fillStyle = "rgba(168,179,196,0.9)";
          g.font = "9px monospace";
          g.fillText(String(n / 4 + 1), x + 3, 9);
        }
      }
      // waveform: outer = full amplitude, inner cyan = bass / kick
      if (wave?.hi.length) {
        const { hi, hiBass, rate } = wave;
        const bw = Math.max(1, px / rate - 0.6);
        const i0 = Math.max(0, Math.floor(start * rate));
        const i1 = Math.min(hi.length, Math.ceil((start + WINDOW) * rate));
        for (let i = i0; i < i1; i++) {
          const x = (i / rate - start) * px;
          const past = i / rate < t;
          const a = (hi[i] / 255) * (mid - 1);
          const b = (hiBass[i] / 255) * (mid - 1) * 0.9;
          g.fillStyle = past ? "rgba(255,255,255,0.28)" : "rgba(255,255,255,0.7)";
          g.fillRect(x, mid - a, bw, a * 2);
          g.fillStyle = past ? "rgba(31,209,255,0.45)" : "#1FD1FF";
          g.fillRect(x, mid - b, bw, b * 2);
        }
      }
      g.fillStyle = "#FFFFFF";
      g.fillRect(w / 2 - 1, 0, 2, h);
      id = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(id);
  }, [w, wave, offset, beat, audioRef]);

  const tap = (e) => {
    const r = ref.current.getBoundingClientRect();
    const t = audioRef.current?.currentTime || 0;
    onSeek(Math.max(0, t + ((e.clientX - r.left) / r.width - 0.5) * WINDOW));
  };

  return <canvas ref={ref} data-testid="player-waveform-detail" onPointerDown={tap} aria-label="Beat view" className="w-full h-12 cursor-pointer touch-none bg-black/30" />;
};

const Overview = ({ wave, time, duration, onSeek }) => {
  const ref = useRef(null);
  const w = useSize(ref);
  const pct = duration ? Math.min(1, time / duration) : 0;

  useEffect(() => {
    const c = ref.current;
    if (!c || !w) return;
    const [g, h] = prep(c, w);
    const peaks = wave?.peaks || [];
    const bass = wave?.bass || [];
    const bars = Math.max(30, Math.floor(w / 2.5));
    const bw = w / bars;
    for (let i = 0; i < bars; i++) {
      const k = Math.floor((i / bars) * (peaks.length || 1));
      const v = peaks.length ? peaks[k] / 100 : 0.08;
      const bv = bass.length ? bass[k] / 100 : 0.5;
      const bh = Math.max(2, v * (h - 2));
      g.fillStyle = i / bars <= pct ? `rgba(31,209,255,${0.45 + bv * 0.55})` : `rgba(255,255,255,${0.22 + bv * 0.6})`;
      g.fillRect(i * bw, (h - bh) / 2, Math.max(1, bw - 0.8), bh);
    }
    g.fillStyle = "#FFFFFF";
    g.fillRect(Math.round(pct * w) - 1, 0, 2, h);
  }, [wave, w, pct]);

  const seekAt = (e) => {
    const r = ref.current.getBoundingClientRect();
    if (duration) onSeek(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * duration);
  };

  return (
    <canvas ref={ref} data-testid="player-waveform" role="slider" aria-label="Seek" aria-valuemin={0} aria-valuemax={Math.round(duration || 0)} aria-valuenow={Math.round(time)}
      className="w-full h-8 cursor-pointer touch-none"
      onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); seekAt(e); }}
      onPointerMove={(e) => { if (e.buttons) seekAt(e); }} />
  );
};

export const Waveform = ({ mix, time, duration, onSeek, audioRef, bpm }) => {
  const wave = useWave(mix?.id);
  return (
    <div className="flex-1 min-w-0 flex flex-col gap-1.5">
      <DetailStrip wave={wave} audioRef={audioRef} bpm={bpm} onSeek={onSeek} />
      <Overview wave={wave} time={time} duration={duration} onSeek={onSeek} />
    </div>
  );
};
